import React, { useState } from 'react';
import Styles from './gridStyle.module.scss';
import { HeartbeatResponse } from '../gridcomponent/grid';
import { useRemedForOSupdate, useThirdPartyupdate } from '../services/dashboardService';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';

interface PatchDetailsModalProps {
  show: boolean;
  selectedRow: {
    patchName: { name: string; image: string; packageIdentifier?: string };
    remediated: string;
    deviceImpactedTotal: string;
    agentList: string[];
    type: 'thirdParty' | 'osUpdate';
    osUpdateId?: string;
    osUpdateType?: string;
    packageIdentifier?: string;
    packageVersion?: string;
    cveScore?: string;
    urlFilter?: string | undefined;
    latestVersionKey?: string;
    selectedDevices?: string;
  } | null;

  heartbeatResponse: HeartbeatResponse | null;
  isLoading: boolean;
  onClose: () => void;
}

const PatchDetailsModal: React.FC<PatchDetailsModalProps> = ({ show, selectedRow, heartbeatResponse, isLoading, onClose }) => {
  const { getRemedForOSupdate } = useRemedForOSupdate();
  const { getThirdPartyupdate } = useThirdPartyupdate();
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [isRemediating, setIsRemediating] = useState(false);
  const { canEdit } = usePermissions();
  const canRemediate = canEdit('remediationPlan');

  if (!show || !selectedRow) return null;

  const handleRemediate = async () => {
    if (!selectedRow || isRemediating) return;
    if (selectedRow.packageVersion === selectedRow.latestVersionKey) {
      onClose();
      addToast('This patch is already at the latest version.', 'info', 4000, 'Remediation');
      return;
    }
    if (
      (!selectedRow.deviceImpactedTotal || selectedRow.deviceImpactedTotal === '0') &&
      (!selectedRow.selectedDevices || selectedRow.selectedDevices === '0')
    ) {
      // If specifically 0 selected devices → show warning
      if (!selectedRow.selectedDevices || selectedRow.selectedDevices === '0') {
        onClose();
        addToast('No devices selected for remediation.', 'warning', 4000, 'Remediation');
      } else {
        onClose();
        addToast('No devices available for remediation.', 'warning', 4000, 'Remediation');
      }
      return;
    }
    setIsRemediating(true);

    try {
      // addToast("Starting remediation process...", "info", 3000, "Remediation");

      if (selectedRow.type === 'osUpdate') {
        const payload = {
          // osUpdateType: selectedRow.type ?? 'Windows',
          osUpdateId: selectedRow.latestVersionKey,
          agentList: selectedRow.agentList,
          selectUpdatesWithoutReboot: false,
        };

        await getRemedForOSupdate(selectedTenantId as string, payload);

        addToast('OS update remediation triggered successfully.', 'success', 4000, 'Remediation Success');
      } else if (selectedRow.type === 'thirdParty') {
        const payload = {
          packageVersion: selectedRow?.latestVersionKey,
          packageIdentifier: selectedRow?.patchName?.packageIdentifier,
          agentList: selectedRow?.agentList,
        };

        await getThirdPartyupdate(selectedTenantId as string, payload);
        addToast(
          `Third-party remediation for ${selectedRow.patchName.name} triggered successfully.`,
          'success',
          4000,
          'Remediation Success',
        );
      }
      onClose();
    } catch (error: any) {
      console.error('Remediation failed:', error);
      onClose();
    } finally {
      setIsRemediating(false);
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    const baseStyle = {
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
      display: 'inline-block',
    };

    const statusLower = status.toLowerCase();

    if (statusLower === 'online' || statusLower === 'active') {
      return { ...baseStyle, backgroundColor: 'rgba(8, 165, 50, 0.2)', color: 'rgba(8, 165, 50, 1)' };
    } else if (statusLower === 'offline' || statusLower === 'inactive') {
      return { ...baseStyle, backgroundColor: 'rgba(165, 8, 17, 0.2)', color: 'rgba(165, 8, 17, 1)' };
    } else {
      return { ...baseStyle, backgroundColor: 'rgba(255, 165, 0, 0.2)', color: 'rgba(255, 165, 0, 1)' };
    }
  };
  return (
    <div className={`fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 ${Styles.modalOverlay}`} onClick={onClose}>
      <div
        className={`bg-[#1c1c1c] rounded-lg shadow-xl text-white ${Styles.modalBox}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '700px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Patch Details</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'all 0.2s',
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                Patch Name
              </label>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>{selectedRow.patchName.name}</p>
            </div>

            <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
              {/* Devices Impacted */}
              {((selectedRow.deviceImpactedTotal && selectedRow.deviceImpactedTotal !== '0') ||
                (selectedRow.selectedDevices && selectedRow.selectedDevices !== '0')) && (
                <div style={{ flex: 1 }}>
                  <label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                    {selectedRow.deviceImpactedTotal ? 'Devices Impacted' : 'Selected Devices'}
                  </label>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>
                    {selectedRow.deviceImpactedTotal ? selectedRow.deviceImpactedTotal : selectedRow.selectedDevices}
                  </p>
                </div>
              )}

              {/*  Version */}
              {selectedRow.packageVersion && (
                <div style={{ flex: 1 }}>
                  <label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                    Current Version
                  </label>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>{selectedRow.packageVersion}</p>
                </div>
              )}
              {/* latest  Version */}
              {selectedRow.latestVersionKey && (
                <div style={{ flex: 1 }}>
                  <label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                    latest Version
                  </label>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>{selectedRow.latestVersionKey}</p>
                </div>
              )}

              {/* CVE Score */}
              {selectedRow.cveScore !== undefined && selectedRow.cveScore !== null && (
                <div style={{ flex: 1 }}>
                  <label
                    style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '13px',
                      display: 'block',
                      marginBottom: '6px',
                    }}
                  >
                    CVE Score
                  </label>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: '500' }}>
                    {selectedRow.cveScore === 0 ? 'N/A' : selectedRow.cveScore}
                  </p>
                </div>
              )}

              {/* Details Button */}
              {((selectedRow.type === 'thirdParty' && selectedRow.urlFilter) ||
                (selectedRow.type === 'osUpdate' && selectedRow.osUpdateId)) && (
                <div style={{ flex: 1 }}>
                  <div className="relative inline-block group">
                    <button
                      onClick={() => {
                        if (selectedRow.type === 'thirdParty' && selectedRow.urlFilter) {
                          navigate(selectedRow.urlFilter);
                        } else if (selectedRow.type === 'osUpdate' && selectedRow.osUpdateId) {
                          const encodedTitle = encodeURIComponent(selectedRow.patchName?.name || '');
                          navigate(
                            `/patchCenter/DeviceNeedingPatch/${selectedTenantId}?type=osUpdate&kbid=${selectedRow.osUpdateId}&title=${encodedTitle}`,
                          );
                        }
                      }}
                      className="p-1 text-white hover:text-white/80 flex items-center justify-center"
                      aria-label="View CVE details"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                        <path
                          d="M12 5c-7.633 0-12 7-12 7s4.367 7 12 7 12-7 12-7-4.367-7-12-7zm0 12c-2.761 
          0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5zm0-8a3 
          3 0 100 6 3 3 0 000-6z"
                        />
                      </svg>
                    </button>

                    {/* Tooltip */}
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs bg-black text-white text-sm rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                      View Affected Devices
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Agent List */}
          {/* <div>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            Affected Agents
                            {heartbeatResponse && (
                                <span style={{
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    padding: '2px 8px',
                                    borderRadius: '10px'
                                }}>
                                    {heartbeatResponse.data.length}
                                </span>
                            )}
                        </h3>

                        {isLoading ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: '16px' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    border: '3px solid rgba(255, 255, 255, 0.1)',
                                    borderTop: '3px solid rgba(255, 255, 255, 0.8)',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }} />
                                <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>Loading agent data...</p>
                                <style>{`
                                        @keyframes spin {
                                            0% { transform: rotate(0deg); }
                                            100% { transform: rotate(360deg); }
                                        }
                                        `}</style>
                            </div>
                        ) : heartbeatResponse && heartbeatResponse.data.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {heartbeatResponse.data.map((agent: AgentHeartbeat) => (
                                    <div
                                        key={agent.id}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '8px',
                                            padding: '16px',
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                            <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>{agent.hostName}</p>
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span
                                                    style={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        backgroundColor: agent.agentStatus.toLowerCase() === 'active' ? '#08A532' : '#A50811',
                                                        display: 'inline-block',
                                                    }}
                                                />
                                                <span style={{ fontSize: '12px', fontWeight: '500', color: 'white' }}>
                                                    {agent.agentStatus}
                                                </span>
                                            </div>

                                        </div>
                                        {agent.heartbeatStatusMessage && (
                                            <p style={{ margin: 0, fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)' }}>
                                                {agent.heartbeatStatusMessage}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '40px 20px',
                                color: 'rgba(255, 255, 255, 0.5)',
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '8px',
                                border: '1px dashed rgba(255, 255, 255, 0.1)'
                            }}>
                                <p style={{ margin: 0 }}>No agent data available</p>
                            </div>
                        )}
                    </div> */}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          {canRemediate && (
            <button
              className={Styles.closeBtn}
              onClick={handleRemediate}
              disabled={isRemediating}
              style={{
                padding: '10px 20px',
                backgroundColor: 'rgba(165, 8, 17, 1)',
                border: 'none',
                borderRadius: '6px',
                color: 'white',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              {isRemediating ? 'Processing...' : 'Remediate'}
            </button>
          )}
          <button
            className={Styles.closeBtn}
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: 'white',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PatchDetailsModal;
